/**
 * Captura do `beforeinstallprompt` do Chromium. Puro, sem React, SSR-safe.
 *
 * **Por que a captura mora num módulo, e não no `useEffect` do componente:** o
 * Chrome dispara o evento assim que os critérios de instalação são satisfeitos —
 * logo depois do boot da aplicação, e uma vez só. Ele **não** é reemitido numa
 * navegação de client-side do App Router. Um ouvinte montado junto com o painel
 * Notificações de `/conta` chegaria sempre depois da festa: o evento já teria
 * passado, o botão nunca apareceria, e o defeito seria invisível em teste (onde
 * o evento é disparado *depois* do `renderHook`). O ouvinte é armado na
 * avaliação deste módulo, que acontece no grafo de import do shell privado.
 *
 * **Não existe no Safari.** O iOS nunca emite este evento e não tem prompt
 * programático nenhum — lá o caminho é a instrução manual, e este módulo
 * simplesmente fica com `null` para sempre. Ausência aqui é contrato de
 * plataforma, nunca falha a investigar.
 */

/**
 * `BeforeInstallPromptEvent` é não-padrão (só Chromium) e não existe no
 * `lib.dom`. Tipo mínimo, com o que de fato usamos.
 */
export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
}

let eventoGuardado: InstallPromptEvent | null = null
const inscritos = new Set<() => void>()

function notificar(): void {
  for (const inscrito of inscritos) inscrito()
}

/**
 * O evento capturado, ou `null` se nunca chegou (ou já foi gasto).
 *
 * Referência estável de propósito: é o snapshot de `useSyncExternalStore`, que
 * entraria em laço infinito com um objeto novo a cada leitura.
 */
export function getInstallPromptEvent(): InstallPromptEvent | null {
  return eventoGuardado
}

/** Assina mudanças do evento guardado. Devolve a função de desinscrição. */
export function subscribeInstallPrompt(inscrito: () => void): () => void {
  inscritos.add(inscrito)
  return () => {
    inscritos.delete(inscrito)
  }
}

/**
 * Descarta o evento guardado e avisa os inscritos.
 *
 * Usado quando ele deixa de valer: consumido pelo diálogo (é de uso único) ou o
 * app foi instalado. Os testes também limpam por aqui — o guardado é estado de
 * módulo e atravessaria um teste para o outro.
 */
export function clearInstallPrompt(): void {
  eventoGuardado = null
  notificar()
}

/**
 * Abre o diálogo nativo de instalação e descarta o evento.
 *
 * O browser **não reemite** o `beforeinstallprompt` depois de consumido, então
 * guardar o evento para um segundo clique só produziria um `prompt()` que não
 * abre nada. Sem evento guardado é no-op: quem não pode oferecer não oferece.
 */
export async function consumeInstallPrompt(): Promise<void> {
  const evento = eventoGuardado
  if (!evento) return
  // Descartado ANTES do await: o diálogo é modal e demorado, e um segundo clique
  // no intervalo chamaria `prompt()` de novo no mesmo evento já em uso.
  eventoGuardado = null
  notificar()
  await evento.prompt()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Sem isto o Chrome mostra o próprio mini-infobar e o botão da tela vira uma
    // segunda oferta da mesma coisa, competindo com a primeira.
    e.preventDefault()
    eventoGuardado = e as InstallPromptEvent
    notificar()
  })
  // Instalado, a oferta perdeu o sentido — e o evento guardado, a validade.
  window.addEventListener('appinstalled', () => {
    clearInstallPrompt()
  })
}
